import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { auditApi } from '../api/admin';
import { usersApi } from '../api/users';
import { Button, Card, EmptyState, ErrorBanner, Field, Input, ListSkeleton, PageHeader, Pagination, Select, Table, Td, Th, Tr } from '../components';
import { cn } from '../lib/cn';
import { formatDateTime } from '../lib/format';

const PAGE_SIZE = 50;
/** Suggestions for the entity type filter; free text is allowed too. */
const ENTITY_TYPES = ['meeting', 'transcript', 'client', 'contact', 'user', 'team', 'kb_doc', 'settings', 'auth'];

export function AuditPage() {
  const [sp, setSp] = useSearchParams();
  const entityType = sp.get('entityType') ?? '';
  const actorId = sp.get('actorId') ?? '';
  const page = Math.max(1, Number(sp.get('page')) || 1);

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!('page' in patch)) next.delete('page');
    setSp(next, { replace: true });
  };

  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list });
  const query = useQuery({
    queryKey: ['audit', { entityType, actorId, page }],
    queryFn: () => auditApi.list({ page, pageSize: PAGE_SIZE, entityType: entityType || undefined, actorId: actorId || undefined }),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <PageHeader title="Audit log" description="Sensitive actions: transcript views, exports, deletions, permission changes." />
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Field label="Entity type" className="w-48">
          <Input list="audit-entity-types" value={entityType} onChange={(e) => update({ entityType: e.target.value.trim() })} placeholder="Any" />
          <datalist id="audit-entity-types">
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
        <Field label="Actor" className="w-56">
          <Select value={actorId} onChange={(e) => update({ actorId: e.target.value })}>
            <option value="">Anyone</option>
            {(users.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </Select>
        </Field>
        {(entityType || actorId) && (
          <Button variant="ghost" onClick={() => setSp({}, { replace: true })}>
            Clear filters
          </Button>
        )}
      </div>
      <Card bodyClassName="p-0">
        {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} className="m-3" />}
        {query.isLoading ? (
          <ListSkeleton />
        ) : query.data?.items.length === 0 ? (
          <EmptyState title="No audit entries" />
        ) : query.data ? (
          <div className={cn(query.isPlaceholderData && 'opacity-60')}>
            <Table>
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Actor</Th>
                  <Th>Action</Th>
                  <Th>Entity</Th>
                  <Th>Details</Th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((e) => (
                  <Tr key={e.id}>
                    <Td className="whitespace-nowrap text-ink-2">{formatDateTime(e.at)}</Td>
                    <Td className="whitespace-nowrap">{e.actorName ?? <span className="text-muted">System</span>}</Td>
                    <Td className="font-mono text-xs">{e.action}</Td>
                    <Td className="text-xs">
                      <span className="font-medium">{e.entityType}</span>
                      {e.entityId && <div className="font-mono text-muted">{e.entityId}</div>}
                    </Td>
                    <Td className="max-w-md">
                      {e.details && Object.keys(e.details).length > 0 ? (
                        <details>
                          <summary className="cursor-pointer text-xs text-accent-ink select-none">Show JSON</summary>
                          <pre className="mt-1 max-h-64 overflow-auto rounded bg-subtle p-2 font-mono text-[11px] text-ink">
                            {JSON.stringify(e.details, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <div className="border-t border-line">
              <Pagination page={page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(p) => update({ page: p > 1 ? String(p) : '' })} />
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
